/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import { AlertTriangle, Lock, Search } from 'lucide-react';
import { FieldLabel } from '../subscriptions/components/shared/SharedComponents';
import navConfig from '../../../layouts/dashboard/NavConfig';

const INPUT_CLASS =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400';
const LOCKED_INPUT_CLASS =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 bg-gray-50 outline-none cursor-not-allowed pr-9';

const METHODS = ['get', 'post', 'put', 'patch', 'delete'];

const LAYOUTS = [
  { value: 'ADMIN', label: 'Admin', hint: 'Super Admin shell' },
  { value: 'CLIENT', label: 'Client', hint: 'Client admin / manager shell' },
  { value: 'CONTENT', label: 'Content', hint: 'Content creator shell' },
  { value: 'USER', label: 'Learner (B2B)', hint: 'Enrolled learner shell' },
  { value: 'DIRECT_USER', label: 'Learner (B2C)', hint: 'Direct consumer shell' },
];

// The selectable pages are the layout's own sidebar entries - NavConfig is where
// the app's pages are actually defined, so the picker cannot drift from reality.
const NAV_BY_LAYOUT = {
  ADMIN: navConfig.adminNavConfig,
  CLIENT: navConfig.clientNavConfig,
  CONTENT: navConfig.contentNavConfig,
};

function pagesForLayout(layout) {
  const sections = NAV_BY_LAYOUT[layout];
  if (!sections) return [];
  const out = [];
  sections.forEach((section) => {
    (section.items || []).forEach((item) => {
      if (item.path) out.push({ path: item.path, title: item.title, section: section.subheader || 'GENERAL' });
      // Nested children (collapsible groups) are pages in their own right.
      (item.children || []).forEach((child) => {
        if (child.path) out.push({ path: child.path, title: `${item.title} › ${child.title}`, section: section.subheader || 'GENERAL' });
      });
    });
  });
  return out;
}
const METHOD_COLOR = {
  get: 'text-emerald-700 bg-emerald-50 border-emerald-300',
  post: 'text-blue-700 bg-blue-50 border-blue-300',
  put: 'text-amber-700 bg-amber-50 border-amber-300',
  patch: 'text-violet-700 bg-violet-50 border-violet-300',
  delete: 'text-red-700 bg-red-50 border-red-300',
};

const MODE_TITLES = { create: 'Add Role', edit: 'Edit Role', view: 'Role' };

const emptyPermissions = () => METHODS.reduce((acc, m) => ({ ...acc, [m]: [] }), {});

// `code` is what the RBAC middleware and live sessions match on, so it is set once.
const CODE_LOCK_HINT = 'Locked — API permission checks and existing sessions match on this value.';

export default function RoleDrawer({ open, mode, record, endpoints, loading, saving, error, onClose, onSubmit }) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [permissions, setPermissions] = useState(emptyPermissions());
  const [layout, setLayout] = useState('');
  const [defaultRoute, setDefaultRoute] = useState('');
  const [allowedPages, setAllowedPages] = useState([]);
  const [tab, setTab] = useState('access');
  const [method, setMethod] = useState('get');
  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);

  const isView = mode === 'view';
  const isCreate = mode === 'create';
  const codeLocked = !isCreate;
  const isSystem = record?.isSystem === true;

  useEffect(() => {
    if (!open) return;
    setMethod('get');
    setSearch('');
    setOnlySelected(false);
    setTab('access');
    if (isCreate) {
      setCode('');
      setDescription('');
      setIsActive(true);
      setPermissions(emptyPermissions());
      setLayout('');
      setDefaultRoute('');
      setAllowedPages([]);
      return;
    }
    setCode(record?.code || '');
    setDescription(record?.description || '');
    setIsActive(record?.isActive !== false);
    setPermissions({ ...emptyPermissions(), ...(record?.permissions || {}) });
    setLayout(record?.layout || '');
    setDefaultRoute(record?.defaultRoute || '');
    setAllowedPages(Array.isArray(record?.allowedPages) ? record.allowedPages : []);
  }, [open, mode, record, isCreate]);

  const pageOptions = useMemo(() => pagesForLayout(layout), [layout]);
  // Warn if the landing page is excluded - routes.js falls back to the first
  // allowed page in that case, which is rarely what the admin intended.
  const landingNotAllowed = Boolean(
    defaultRoute && allowedPages.length > 0 && !allowedPages.includes(defaultRoute)
  );

  const available = useMemo(() => endpoints?.[method] || [], [endpoints, method]);
  const selected = useMemo(() => new Set(permissions[method] || []), [permissions, method]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return available.filter((p) => {
      if (onlySelected && !selected.has(p)) return false;
      return !term || p.toLowerCase().includes(term);
    });
  }, [available, search, onlySelected, selected]);

  const togglePath = (path) => {
    if (isView) return;
    setPermissions((prev) => {
      const list = new Set(prev[method] || []);
      if (list.has(path)) list.delete(path);
      else list.add(path);
      return { ...prev, [method]: [...list] };
    });
  };

  const toggleAllVisible = () => {
    if (isView) return;
    setPermissions((prev) => {
      const list = new Set(prev[method] || []);
      const allOn = visible.every((p) => list.has(p));
      visible.forEach((p) => (allOn ? list.delete(p) : list.add(p)));
      return { ...prev, [method]: [...list] };
    });
  };

  const totalSelected = METHODS.reduce((n, m) => n + (permissions[m]?.length || 0), 0);
  const canSubmit = !saving && description.trim() && (!isCreate || /^[A-Za-z][A-Za-z0-9_]{2,49}$/.test(code.trim()));

  const handleSubmit = () => {
    if (isView) return;
    const payload = {
      description: description.trim(),
      isActive,
      permissions,
      layout: layout || null,
      defaultRoute: defaultRoute.trim() || null,
      // Empty means "no restriction" - the API normalises [] to null.
      allowedPages,
    };
    if (isCreate) payload.code = code.trim().toUpperCase();
    onSubmit(payload);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '720px' },
          borderLeft: '1px solid #e5e7eb',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900">{MODE_TITLES[mode] || 'Role'}</h2>
          {!isCreate && record?.code && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              {record.code}
              {record.userCount !== undefined && ` · ${record.userCount} user${record.userCount === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
        <IconButton size="small" onClick={onClose} sx={{ color: '#6b7280' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* ── Body ── */}
      <div className="px-6 py-5 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {isSystem && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <p className="text-[12.5px] text-amber-800">
                  Built-in role. Its permissions can be edited, but it cannot be renamed or deleted.
                </p>
              </div>
            )}

            {record?.usesJsonFallback && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-blue-500 shrink-0" />
                <p className="text-[12.5px] text-blue-800">
                  This role has no stored permissions yet, so it is still using the built-in defaults.
                  Saving will store them in the database.
                </p>
              </div>
            )}

            <div className="mb-4">
              <FieldLabel required={isCreate}>Role Code</FieldLabel>
              {codeLocked ? (
                <div className="relative">
                  <input type="text" value={code} disabled className={LOCKED_INPUT_CLASS} />
                  <Lock className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                </div>
              ) : (
                <input
                  type="text"
                  value={code}
                  placeholder="e.g. MARKETING_USER"
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className={INPUT_CLASS}
                />
              )}
              <p className="text-xs text-gray-400 mt-1">
                {codeLocked ? CODE_LOCK_HINT : 'Uppercase letters, digits and underscores. Cannot be changed later.'}
              </p>
            </div>

            <div className="mb-4">
              <FieldLabel required>Description</FieldLabel>
              <input
                type="text"
                value={description}
                maxLength={100}
                disabled={isView}
                placeholder="e.g. Marketing user"
                onChange={(e) => setDescription(e.target.value)}
                className={isView ? LOCKED_INPUT_CLASS : INPUT_CLASS}
              />
            </div>

            <div className="mb-5">
              <label htmlFor="role-active" className="flex items-center gap-2 cursor-pointer">
                <input
                  id="role-active"
                  type="checkbox"
                  checked={isActive}
                  disabled={isView}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="accent-blue-600"
                />
                <span className="text-[13px] text-gray-700">Active</span>
              </label>
              <p className="text-xs text-gray-400 mt-1">Inactive roles fall back to the built-in defaults.</p>
            </div>

            {/* ── Section tabs ── */}
            <div className="flex border-b border-gray-200 mb-4">
              {[['access', 'Pages & Layout'], ['api', 'API Permissions']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 text-sm whitespace-nowrap -mb-px cursor-pointer bg-transparent border-0 border-b-2 transition-colors duration-150
                    ${tab === key
                      ? 'font-semibold text-blue-600 border-blue-600'
                      : 'font-normal text-gray-500 border-transparent hover:text-gray-900'}`}
                >
                  {label}
                  <span className="ml-1.5 text-[11px] text-gray-400">
                    {key === 'access' ? (allowedPages.length || 'all') : totalSelected}
                  </span>
                </button>
              ))}
            </div>

            {tab === 'access' && (
              <>
                <div className="mb-4">
                  <FieldLabel>Layout</FieldLabel>
                  <select
                    value={layout}
                    disabled={isView}
                    onChange={(e) => { setLayout(e.target.value); setAllowedPages([]); setDefaultRoute(''); }}
                    className={`${isView ? LOCKED_INPUT_CLASS : INPUT_CLASS} cursor-pointer`}
                  >
                    <option value="">Not set — falls back to the built-in mapping</option>
                    {LAYOUTS.map((l) => (
                      <option key={l.value} value={l.value}>{l.label} — {l.hint}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Which shell and route tree this role loads after login.
                  </p>
                </div>

                <div className="mb-4">
                  <FieldLabel>Landing page after login</FieldLabel>
                  <select
                    value={defaultRoute}
                    disabled={isView || pageOptions.length === 0}
                    onChange={(e) => setDefaultRoute(e.target.value)}
                    className={`${isView ? LOCKED_INPUT_CLASS : INPUT_CLASS} cursor-pointer`}
                  >
                    <option value="">Not set — uses the layout default</option>
                    {pageOptions.map((pg) => (
                      <option key={pg.path} value={pg.path}>{pg.title} ({pg.path})</option>
                    ))}
                  </select>
                  {landingNotAllowed && (
                    <p className="text-xs text-amber-600 mt-1">
                      This page is not in the allowed list below. Tick it, or the role will be redirected elsewhere.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between mb-2">
                  <p className="text-[13px] font-semibold text-gray-800">Accessible Pages</p>
                  <span className="text-[11.5px] text-gray-400">
                    {allowedPages.length === 0
                      ? 'All pages in this layout'
                      : `${allowedPages.length} of ${pageOptions.length} selected`}
                  </span>
                </div>

                {pageOptions.length === 0 ? (
                  <p className="text-[12.5px] text-gray-400 border border-gray-200 rounded-lg px-3 py-6 text-center">
                    {layout
                      ? 'This layout has no sidebar pages to restrict.'
                      : 'Pick a layout above to choose its pages.'}
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                      <input
                        type="checkbox"
                        disabled={isView}
                        checked={allowedPages.length > 0 && allowedPages.length === pageOptions.length}
                        onChange={() => setAllowedPages(
                          allowedPages.length === pageOptions.length ? [] : pageOptions.map((pg) => pg.path)
                        )}
                        className="accent-blue-600"
                      />
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Select all
                      </span>
                      {allowedPages.length > 0 && !isView && (
                        <button
                          type="button"
                          onClick={() => setAllowedPages([])}
                          className="ml-auto text-[11px] font-semibold text-blue-600 bg-transparent border-0 cursor-pointer"
                        >
                          Clear (allow all)
                        </button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {pageOptions.map((pg, i) => {
                        const newSection = i === 0 || pageOptions[i - 1].section !== pg.section;
                        const checked = allowedPages.includes(pg.path);
                        return (
                          <div key={pg.path}>
                            {newSection && (
                              <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50/70">
                                {pg.section}
                              </p>
                            )}
                            <label
                              htmlFor={`page-${pg.path}`}
                              className={`flex items-center gap-2 px-3 py-1.5 border-b border-gray-50 last:border-b-0 ${
                                isView ? '' : 'cursor-pointer hover:bg-gray-50'
                              } ${checked ? 'bg-blue-50/60' : ''}`}
                            >
                              <input
                                id={`page-${pg.path}`}
                                type="checkbox"
                                checked={checked}
                                disabled={isView}
                                onChange={() => setAllowedPages((prev) => (
                                  prev.includes(pg.path)
                                    ? prev.filter((x) => x !== pg.path)
                                    : [...prev, pg.path]
                                ))}
                                className="accent-blue-600 shrink-0"
                              />
                              <span className="text-[12.5px] text-gray-700 truncate">{pg.title}</span>
                              <span className="ml-auto text-[11px] font-mono text-gray-400 truncate">{pg.path}</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <p className="text-[11.5px] text-gray-400 mt-2">
                  Leaving this empty gives the role every page in its layout. Selecting pages also
                  filters the sidebar, and blocked routes redirect to the landing page.
                </p>
              </>
            )}

            {tab === 'api' && (
            <>
            {/* ── Permissions ── */}
            <div className="flex items-center justify-between mb-2">
              <p className="text-[13px] font-semibold text-gray-800">API Permissions</p>
              <span className="text-[11.5px] text-gray-400">{totalSelected} endpoint{totalSelected === 1 ? '' : 's'} granted</span>
            </div>

            <div className="flex border-b border-gray-200 mb-3 overflow-x-auto">
              {METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMethod(m); setSearch(''); }}
                  className={`px-3.5 py-2 text-sm whitespace-nowrap -mb-px cursor-pointer bg-transparent border-0 border-b-2 transition-colors duration-150
                    ${method === m
                      ? 'font-semibold text-blue-600 border-blue-600'
                      : 'font-normal text-gray-500 border-transparent hover:text-gray-900'}`}
                >
                  {m.toUpperCase()}
                  <span className="ml-1.5 text-[11px] text-gray-400">{permissions[m]?.length || 0}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  placeholder={`Search ${available.length} ${method.toUpperCase()} endpoints`}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`${INPUT_CLASS} pl-8 py-1.5 text-[13px]`}
                />
              </div>
              <button
                type="button"
                onClick={() => setOnlySelected((v) => !v)}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium border cursor-pointer ${
                  onlySelected ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Selected only
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <input
                  type="checkbox"
                  disabled={isView || visible.length === 0}
                  checked={visible.length > 0 && visible.every((p) => selected.has(p))}
                  onChange={toggleAllVisible}
                  className="accent-blue-600"
                />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {visible.length} shown · {selected.size} selected
                </span>
                <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded border ${METHOD_COLOR[method]}`}>
                  {method.toUpperCase()}
                </span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {visible.length === 0 ? (
                  <p className="text-[13px] text-gray-400 text-center py-8">No endpoints match.</p>
                ) : (
                  visible.map((path) => (
                    <label
                      key={path}
                      htmlFor={`perm-${method}-${path}`}
                      className={`flex items-center gap-2 px-3 py-1.5 border-b border-gray-50 last:border-b-0 ${
                        isView ? '' : 'cursor-pointer hover:bg-gray-50'
                      } ${selected.has(path) ? 'bg-blue-50/60' : ''}`}
                    >
                      <input
                        id={`perm-${method}-${path}`}
                        type="checkbox"
                        checked={selected.has(path)}
                        disabled={isView}
                        onChange={() => togglePath(path)}
                        className="accent-blue-600 shrink-0"
                      />
                      <span className="text-[12.5px] font-mono text-gray-700 truncate">{path}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            </>
            )}
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="px-6 py-4 border-t border-gray-100 flex gap-2.5 justify-end flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
        >
          {isView ? 'Close' : 'Cancel'}
        </button>
        {!isView && (
          <LoadingButton
            loading={Boolean(saving)}
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              background: '#2563eb', borderRadius: '7px', textTransform: 'none',
              fontSize: '13px', fontWeight: 600, px: '20px',
              '&:hover': { background: '#1d4ed8' },
            }}
          >
            {isCreate ? 'Create Role' : 'Save Changes'}
          </LoadingButton>
        )}
      </div>
    </Drawer>
  );
}
